import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/primitives/button'
import { Input } from '@neo/primitives/input'
import { Label } from '@neo/primitives/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import { Textarea } from '@neo/primitives/textarea'
import { Typography } from '@neo/primitives/typography'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import type { Node } from '@xyflow/react'
import { useUpdateNodeInternals } from '@xyflow/react'
import capitalize from 'lodash/capitalize'
import { Plus, Sparkles, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'router-v6'
import { EnhancedTextArea } from 'v2/shared/components/enhanced-textarea'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { useLivekitEnabled } from 'v2/shared/hooks/use-livekit-enabled'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { AgentChannels } from 'v2/types'
import { errorToast } from 'v2/utils/toasts'
import { z } from 'zod'
import { NodevarList } from '../components/nodevar-list'
import { v2validations } from '../config/validation.config'
import { DEFAULT_INITIAL_WAIT_TIME, DEFAULT_LLM_MODEL_ID, DEFAULT_LLM_PROVIDER } from '../constants'
import { useTypedReactFlow } from '../hooks/use-react-flow'
import { useSuggestCollectionConfigurationMutation } from '../queries'
import { useFlowStore } from '../store'
import {
  type AgentMeta,
  type AiAgentV2NodeData,
  FlowNodeTypes,
  type RFInstance,
  type SidebarComponentProps,
} from '../types'
import { scheduleNodeVariableReferenceRewrite } from '../utils'
import { isCallAiSourceNode } from '../utils/ai-agent-utils'
import { isV210Flow } from '../utils/ai-agent-v29'
import { getConnectedNoReplyNode, syncNoReplyTarget } from '../utils/no-reply-graph'
import {
  getConnectedVoicemailNode,
  getVoicemailIntent,
  syncVoicemailTarget,
} from '../utils/voicemail-graph'
import { AiAgentV2Actions, validateAiAgentV2Actions } from './ai-agents/ai-agent-v2-actions'
import { channelOptions } from './ai-agents/channel-options'
import { CollectionConfiguration } from './ai-agents/collection-configuration'
import {
  applyChanges,
  authoredOf,
  detectChanges,
  detectCollections,
  type InputRowValue,
  mergeFilled,
  reconcileDetected,
  requestFor,
  rowSignature,
  STALE_ANSWER_NOTICE,
  toSaved,
  toWire,
} from './ai-agents/collection-configuration-config'
import { withDetectedInputs } from './ai-agents/inputs'
import { callStartBehaviorField } from './screening-shared'
import { GenericSidebarComponent } from './sidebar.base'

/**
 * BFS traversal to check path from target node backwards to find trigger nodes
 * Returns which trigger types were found and if any ai_agent_v2 exists in the path
 */
const checkPathToTriggers = (
  targetNodeId: string,
  rf: RFInstance,
): { foundStart: boolean; foundInitiateCall: boolean; hasAiAgentV2InPath: boolean } => {
  const nodes = rf.getNodes()
  const edges = rf.getEdges()
  const nodeMap = new Map(nodes.map((n) => [n.id, n]))
  const visited = new Set<string>()

  const queue: string[] = [targetNodeId]
  let foundStart = false
  let foundInitiateCall = false
  let hasAiAgentV2InPath = false

  while (queue.length > 0) {
    const currentId = queue.shift()
    if (currentId === undefined || visited.has(currentId)) continue
    visited.add(currentId)

    const predecessorEdges = edges.filter((edge) => edge.target === currentId)

    for (const edge of predecessorEdges) {
      const predecessorNode = nodeMap.get(edge.source)
      if (!predecessorNode) continue

      if (predecessorNode.type === FlowNodeTypes.initiate_call) {
        foundInitiateCall = true
      } else if (predecessorNode.type === FlowNodeTypes.start) {
        foundStart = true
      } else if (isCallAiSourceNode(predecessorNode)) {
        // Call-channel AI predecessors (incl. screening nodes) mean the
        // current node is not first-level.
        hasAiAgentV2InPath = true
      }

      if (!visited.has(edge.source)) {
        queue.push(edge.source)
      }
    }
  }

  return { foundStart, foundInitiateCall, hasAiAgentV2InPath }
}

/**
 * Helper to get trigger path info for the selected call node
 * Returns null if not a call channel or no valid node/flow instance
 */
const getCallNodeTriggerInfo = (channel: string) => {
  if (channel !== 'call') return null

  const selectedNodeID = useFlowStore.getState().selectedNodeID
  const rf = useFlowStore.getState().flowInstance

  if (!rf || !selectedNodeID) return null

  return checkPathToTriggers(selectedNodeID, rf)
}

/**
 * For allow_greeting_interruption - shows for both inbound (start) and outbound (initiate_call)
 * First-level AI call nodes only (no ai_agent_v2 in the path)
 */
const shouldShowCallOnlyFields = (channel: string) => {
  const triggerInfo = getCallNodeTriggerInfo(channel)
  if (!triggerInfo) return false

  const { foundStart, foundInitiateCall, hasAiAgentV2InPath } = triggerInfo
  return (foundStart || foundInitiateCall) && !hasAiAgentV2InPath
}

/**
 * Channels whose backend walks the AI node's "no reply" exit on reply timeout,
 * i.e. where the On-AI-timeout branch is offered. WhatsApp exposes the wait
 * controls but its runtime leg is unverified, so it stays excluded.
 */
const channelSupportsTimeoutBranch = (channel: string | undefined) =>
  channel === 'chat' || channel === 'message'

/**
 * For voicemail and screening fields - shows only for outbound calls (initiate_call)
 * First-level AI call nodes only (no ai_agent_v2 in the path)
 */
const shouldShowOutboundCallOnlyFields = (channel: string) => {
  const triggerInfo = getCallNodeTriggerInfo(channel)
  if (!triggerInfo) return false

  const { foundInitiateCall, hasAiAgentV2InPath } = triggerInfo
  return foundInitiateCall && !hasAiAgentV2InPath
}

type FormValues = AiAgentV2NodeData['config']

// The org this node is saved under, read from the profile the app shell already fetched.
export const orgAuthId = (client: QueryClient): string | undefined => {
  const profile = client.getQueryData<{
    organizations?: Array<{ org_auth_id?: string; auth_id?: string }>
  }>(getSettingsQueryKeys().profile)
  const org = profile?.organizations?.[0]
  return org?.org_auth_id || org?.auth_id || undefined
}

// A whole-call detection failure (no auth id, agent-runner unreachable) is one line on the tab.
// A blank Input row of the sidebar's shape, for an Input the runner proposed.
const blankInputRow = (proposal: { variable_name: string; variable_instructions: string }) => ({
  ...proposal,
  variable_format_instructions: '',
  variable_type: 'string',
  variable_enum_values: [],
  configure_data_format: false,
  id: crypto.randomUUID(),
})

const nameOf = (row: InputRowValue) => (row.variable_name ?? '').trim()

const wording = (text: string) => text.trim().replace(/\s+/g, ' ')

// what a v2.10 description was written for
const keyOf = (row: InputRowValue) =>
  `${nameOf(row)}\n${row.collection?.type ?? ''}\n${row.variable_instructions ?? ''}`

// a field the author typed in is theirs, and Jev and the description writer leave it until it is emptied
const markAuthored =
  (field: 'name' | 'description') =>
  (_values: FormValues, index?: number, form?: GenericFormType<FormValues>) => {
    const row = form?.getFieldValue(`extract_variables[${index}]` as never) as
      | InputRowValue
      | undefined
    if (!row?.collection) return
    const typed = field === 'name' ? row.variable_name : row.variable_instructions
    form?.setFieldValue(
      `extract_variables[${index}].collection` as never,
      {
        ...row.collection,
        overrides: { ...row.collection.overrides, [field]: typed?.trim() ? true : undefined },
      } as never,
    )
  }

/** The rows a detection added: the ones it was not asked about. */
const addedBy = (asked: InputRowValue[], rows: InputRowValue[]) => {
  const ids = new Set(asked.map((row) => row.id))
  return rows.filter((row) => !ids.has(row.id))
}

const CollectionDetectionBanner = () => {
  const failure = useFlowStore((state) => state.collectionDetection.failure)
  const notice = useFlowStore((state) => state.collectionDetection.notice)
  if (!failure && !notice) return null
  return (
    <div
      className={`mb-3 rounded-md bg-bg3 p-2.5 text-s-400 ${failure ? 'text-status-danger' : 'text-secondary'}`}
      data-testid={
        failure ? 'text-collection-detection-failure' : 'text-collection-detection-notice'
      }
    >
      {failure ?? notice}
    </div>
  )
}

// A pass the next node's pass has taken over from must not unlock that node when it settles.
const endDetection = (nodeId: string) => {
  const { collectionDetection, setCollectionDetection } = useFlowStore.getState()
  if (collectionDetection.running === nodeId) setCollectionDetection({ running: null })
}

// Asks agent-runner which Inputs the Task needs and configures the ones already listed.
const DetectInputsButton = ({
  form,
  isEditing,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: the sidebar form is generic over the node's config
  form: any
  isEditing: boolean
}) => {
  const queryClient = useQueryClient()
  const suggestCollection = useSuggestCollectionConfigurationMutation()
  const setCollectionDetection = useFlowStore((state) => state.setCollectionDetection)
  const writeDetection = useFlowStore((state) => state.writeDetection)
  const detectingNodeId = useFlowStore((state) => state.collectionDetection.running)
  const selectedNodeID = useFlowStore((state) => state.selectedNodeID)
  const running = !!detectingNodeId && detectingNodeId === selectedNodeID

  const detect = async () => {
    const instructions = (form.getFieldValue('instructions') ?? '') as string
    const rows = (form.getFieldValue('extract_variables') ?? []) as InputRowValue[]
    // the row controls stay live while this is in flight, so bind the answer to what was asked
    const request = requestFor(useFlowStore.getState().selectedNodeID ?? '', rows, instructions)
    setCollectionDetection({ running: request.nodeId })
    writeDetection(request.nodeId, { errors: {}, failure: null, notice: null })
    try {
      const result = await detectCollections({
        authId: orgAuthId(queryClient),
        instructions,
        rows,
        suggest: suggestCollection.mutateAsync,
        propose: true,
        newRow: blankInputRow,
      })
      const now = (form.getFieldValue('extract_variables') ?? []) as InputRowValue[]
      const nodeNow = useFlowStore.getState().selectedNodeID ?? ''
      const merged = reconcileDetected(
        now,
        result.rows,
        request,
        nodeNow,
        (form.getFieldValue('instructions') ?? '') as string,
      )
      if (!merged) {
        // a node that is no longer open has no banner of its own; a Task edit does
        writeDetection(request.nodeId, { errors: {}, failure: null, notice: STALE_ANSWER_NOTICE })
        return
      }
      form.setFieldValue('extract_variables', merged)
      // a proposal the author saw here counts the same as one a save added
      if (!result.failure)
        useFlowStore
          .getState()
          .markInputsProposed(request.nodeId, addedBy(rows, result.rows).map(nameOf))
      writeDetection(request.nodeId, {
        errors: result.errors,
        failure: result.failure,
        notice: result.notice,
      })
    } finally {
      endDetection(request.nodeId)
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={!isEditing || running}
      title="Ask the agent what this node's instructions need to collect"
      data-testid="button-detect-inputs"
      onClick={detect}
    >
      <Sparkles className="mr-1.5 size-3.5" />
      {running ? 'Detecting…' : 'Detect from instructions'}
    </Button>
  )
}

const fields = (
  nodeVars: string[],
  setAgentMeta: (meta: AgentMeta) => void,
  slackTriggerEnabled: boolean,
  selectedNode: Node<AiAgentV2NodeData>,
  isV210: boolean,
  describingDots: number,
  filling: boolean,
  saving: boolean,
  fill: (form: GenericFormType<FormValues>) => void,
): GenericFieldConfig<FormValues> => {
  const isV29 = selectedNode.type === FlowNodeTypes.ai_agent_v29
  const validations = v2validations({ nodeVars })[
    isV29 ? FlowNodeTypes.ai_agent_v29 : FlowNodeTypes.ai_agent_v2
  ]

  return [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validations.shape.name,
      componentType: 'input',
    },
    {
      name: 'channel',
      label: 'Channel',
      zodValidation: validations.shape.channel,
      componentType: 'select',
      inputProps: {
        options: channelOptions(isV29, slackTriggerEnabled),
      },
      afterOnChange(state) {
        if (state.channel) {
          setAgentMeta({
            defaultChannel: state.channel as AgentChannels,
          })
        }
      },
    },
    {
      name: 'update_similar_nodes',
      componentType: 'checkbox',
      getDynamicProps: (state) => {
        const currentChannel = state.channel
        const originalChannel = selectedNode.data.config.channel
        return {
          inputProps: {
            label: `Update the channel to ${capitalize(currentChannel)} for all ${capitalize(originalChannel)} AI Nodes`,
            labelClassName: 'text-s-400 text-foreground',
            className:
              'bg-status-warning/[0.08] p-2 rounded-md -mt-2 [&>button[role="checkbox"]]:bg-background [&>button[role="checkbox"]]:border-st1 [&>button[role="checkbox"][data-state="checked"]]:bg-primary [&>button[role="checkbox"][data-state="checked"]]:text-primary-foreground',
          },
        }
      },
      visible: (formValues) => {
        const currentChannel = formValues.channel
        const originalChannel = selectedNode.data.config.channel
        return !!originalChannel && !!currentChannel && currentChannel !== originalChannel // Show checkbox only if the channel has changed and the node isn't new
      },
      onChangeListenTo: ['channel'],
      zodValidation: z.boolean().optional(),
    },
    {
      name: 'tabs',
      type: 'basic',
      component: ({ form }) => {
        const channel = form.useStore((state) => state.values.channel)
        const { isLivekitEnabled } = useLivekitEnabled()
        const isEditing = useFlowStore((state) => state.isEditing)
        const isCallChannel = channel === 'call'
        // Chat and SMS AI nodes expose Actions too, mirroring voice: authored
        // actions flow through to the agent as tool calls (build_tools).
        const isChatChannel = channel === 'chat'
        const isMessageChannel = channel === 'message'
        const showActionsTab =
          (isCallChannel || isChatChannel || isMessageChannel) && isLivekitEnabled
        const [activeTab, setActiveTab] = useState('instructions')
        // v2.10 variables are made from the instructions: with none there is nothing to show
        const instructions = form.useStore((state) => state.values.instructions) as string
        const noVariables = isV210 && !isEmbedMode() && !wording(instructions ?? '')
        // Controlled tab state survives re-renders/feature-flag resolution. Fall
        // back to instructions when the actions tab is selected but no longer
        // shown (e.g. livekit flag flips off), so Radix never lands on a tab with
        // no trigger/content. (SER-5593)
        const currentTab =
          (activeTab === 'actions' && !showActionsTab) || (activeTab === 'variables' && noVariables)
            ? 'instructions'
            : activeTab
        return (
          <Tabs
            value={currentTab}
            onValueChange={(next) => {
              if (currentTab === 'instructions' && next === 'variables') fill(form)
              setActiveTab(next)
            }}
            className="col-span-2"
          >
            <TabsList variant="default" className="mb-4 flex ">
              <TabsTrigger
                value="instructions"
                variant="default"
                className="flex-1 justify-center instructions"
              >
                Instructions
              </TabsTrigger>
              <TabsTrigger
                value="variables"
                variant="default"
                className="flex-1 justify-center variables"
                disabled={noVariables}
              >
                {isV29 ? 'Inputs' : 'Variables'}
              </TabsTrigger>
              {showActionsTab && (
                <TabsTrigger
                  value="actions"
                  variant="default"
                  className="flex-1 justify-center actions"
                >
                  Actions
                </TabsTrigger>
              )}
            </TabsList>
            <TabsContent
              value="instructions"
              forceMount
              className="data-[state=inactive]:hidden instructions flex flex-col gap-4"
            >
              <FieldsRenderer<FormValues>
                form={form}
                fields={[
                  {
                    label: 'Add instructions',
                    name: 'instructions',
                    type: 'custom',
                    component: ({ value, onChange, ...props }) => (
                      // biome-ignore lint/a11y/noStaticElementInteractions: catches the rich-text editor's focusout
                      <div
                        onBlur={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget as Element | null))
                            fill(form)
                        }}
                      >
                        <EnhancedTextArea
                          value={value as string}
                          onChange={onChange}
                          className="min-h-[30px]"
                          {...props}
                        />
                      </div>
                    ),
                    eventDataExtractor: (e) => e,
                    // a v2.10 save writes the instructions of its click, and its rows must be for them
                    disabled: () => saving,
                    zodValidation: z.string().min(1, 'Instructions are required'),
                    inputProps: {
                      placeholder:
                        'Provide detailed instructions to guide the AI on how to handle users queries.',
                      rows: 4,
                      endIcon: (
                        <NodevarList
                          nodevars={nodeVars}
                          inputElementSelector="#instructions .ql-ref"
                        />
                      ),
                      // className: 'mt-2.5',
                      'data-testid': 'textarea-ai-instructions',
                    },
                  },
                  {
                    name: 'allow_greeting_interruption',
                    componentType: 'checkbox',
                    label: 'Enable interruption for the greeting message',
                    zodValidation: validations.shape.allow_greeting_interruption,
                    visible: (values: FormValues) => shouldShowCallOnlyFields(values.channel),
                    onChangeListenTo: ['channel'],
                    getDynamicProps: (state) => {
                      const isOutbound = shouldShowOutboundCallOnlyFields(state.channel)
                      return {
                        inputProps: {
                          'data-testid': 'checkbox-allow-greeting-interruption',
                          ...(isOutbound && {
                            helperText:
                              'Voicemail and Screening works best when interruption is enabled',
                            helperTextClassName: 'text-s-400 text-secondary',
                          }),
                        },
                      }
                    },
                  },
                  {
                    name: 'voicemail_detection_next_node',
                    type: 'basic',
                    component: ({ form }) => {
                      const channel = form.useStore((state) => state.values.channel)

                      if (!shouldShowOutboundCallOnlyFields(channel)) return null

                      return (
                        <div className="col-span-2 rounded-md border border-st2 p-4 space-y-4">
                          <Typography variant="H4/500" className="text-foreground">
                            On voicemail detection
                          </Typography>
                          <FieldsRenderer<FormValues>
                            form={form}
                            fields={[
                              {
                                name: 'voicemail_detection_next_node',
                                componentType: 'select',
                                zodValidation: validations.shape.voicemail_detection_next_node,
                                visible: (values) =>
                                  shouldShowOutboundCallOnlyFields(values.channel),
                                inputProps: {
                                  options: [
                                    {
                                      label: 'End Conversation',
                                      value: FlowNodeTypes.end_conversation,
                                    },
                                    { label: 'Play Audio', value: FlowNodeTypes.prompt },
                                  ],
                                  placeholder: 'Select next node',
                                  clearable: false,
                                },
                                onChangeListenTo: ['channel'],
                              },
                            ]}
                          />
                        </div>
                      )
                    },
                  },
                  {
                    name: 'screening_detection_section',
                    type: 'basic',
                    component: ({ form }) => {
                      const channel = form.useStore((state) => state.values.channel)
                      const voicemailDetectionEnabled = form.useStore(
                        (state) => !!state.values.voicemail_detection_next_node,
                      )

                      if (!shouldShowOutboundCallOnlyFields(channel) || !voicemailDetectionEnabled)
                        return null

                      return (
                        <div className="col-span-2 rounded-md border border-st2 p-4 space-y-4">
                          <Typography variant="H4/500" className="text-foreground">
                            On screening detection
                          </Typography>
                          <FieldsRenderer<FormValues>
                            form={form}
                            fields={[
                              {
                                name: 'screening_name',
                                componentType: 'input',
                                zodValidation: validations.shape.screening_name,
                                inputProps: {
                                  placeholder: "Hi, I'm calling from...",
                                  label: "Who's calling",
                                  endIcon: (
                                    <NodevarList
                                      nodevars={nodeVars}
                                      inputElementSelector='input[name="screening_name"]'
                                    />
                                  ),
                                },
                              },
                              {
                                name: 'screening_reason',
                                componentType: 'input',
                                zodValidation: validations.shape.screening_reason,
                                inputProps: {
                                  placeholder: "I'm calling to...",
                                  label: 'Call purpose',
                                  endIcon: (
                                    <NodevarList
                                      nodevars={nodeVars}
                                      inputElementSelector='input[name="screening_reason"]'
                                    />
                                  ),
                                },
                              },
                            ]}
                          />
                        </div>
                      )
                    },
                  },
                  callStartBehaviorField<FormValues>(validations.shape.initial_wait_time, {
                    visible: (values) => shouldShowOutboundCallOnlyFields(values.channel),
                    onChangeListenTo: ['channel'],
                  }),
                  {
                    name: 'wait_for_response',
                    label: 'Wait for response',
                    componentType: 'checkbox',
                    zodValidation: validations.shape.wait_for_response,
                    visible: (values: FormValues) => values.channel !== 'call',
                    onChangeListenTo: ['channel'],
                  },
                  {
                    name: 'reply_timeout',
                    label: 'Reply timeout',
                    componentType: 'inputNumber',
                    zodValidation: validations.shape.reply_timeout,
                    visible: (values: FormValues) =>
                      values.wait_for_response === true && values.channel !== 'call',
                    onChangeListenTo: ['wait_for_response', 'channel'],
                    inputProps: {
                      min: 60,
                      max: 3600,
                    },
                  },
                  {
                    name: 'no_reply_next_node',
                    type: 'basic',
                    component: ({ form }) => {
                      const channel = form.useStore((state) => state.values.channel)
                      const waitForResponse = form.useStore(
                        (state) => state.values.wait_for_response,
                      )

                      if (!channelSupportsTimeoutBranch(channel) || !waitForResponse) return null

                      return (
                        <div className="col-span-2 rounded-md border border-st2 p-4 space-y-4">
                          <Typography variant="H4/500" className="text-foreground">
                            On AI timeout
                          </Typography>
                          <FieldsRenderer<FormValues>
                            form={form}
                            fields={[
                              {
                                name: 'no_reply_next_node',
                                componentType: 'select',
                                zodValidation: validations.shape.no_reply_next_node,
                                visible: (values) =>
                                  channelSupportsTimeoutBranch(values.channel) &&
                                  values.wait_for_response === true,
                                inputProps: {
                                  options: [
                                    {
                                      label: 'End Conversation',
                                      value: FlowNodeTypes.end_conversation,
                                    },
                                    {
                                      label: 'Send Message',
                                      value: FlowNodeTypes.agent_preset_conversation,
                                    },
                                  ],
                                  placeholder: 'Select next node',
                                  clearable: false,
                                },
                                onChangeListenTo: ['channel', 'wait_for_response'],
                              },
                            ]}
                          />
                        </div>
                      )
                    },
                  },
                ]}
              />
              {/* <AIInstructions form={form} nodeVars={nodeVars} flowNodeType={flowNodeType} /> */}
            </TabsContent>
            <TabsContent
              value="variables"
              forceMount
              className="data-[state=inactive]:hidden variables"
            >
              <CollectionDetectionBanner />
              {filling && (
                <Typography
                  variant="XS/400"
                  className="mb-3 text-secondary"
                  data-testid="text-variables-filling"
                >
                  Filling the variables from the instructions…
                </Typography>
              )}
              {form.useStore(
                (state) =>
                  !!state.values.voicemail_detection_next_node &&
                  shouldShowOutboundCallOnlyFields(state.values.channel),
              ) && (
                <div className="flex flex-col gap-4 bg-bg3 p-4 rounded-md mb-4">
                  <div className="space-y-2">
                    <Label className="text-p-500">Variable name</Label>
                    <Input
                      value="voicemail_detected"
                      disabled
                      placeholder="Enter variable name to extract"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-p-500">Variable instructions</Label>
                    <Textarea
                      value="Automatically set to true when voicemail is detected during the call"
                      disabled
                      className="min-h-[120px] bg-background"
                      data-testid="textarea-voicemail-variable-instructions"
                    />
                  </div>
                </div>
              )}
              {/* v2.10 rows come from the instructions; a value is added by mentioning it there */}
              {(!isV210 || isEmbedMode()) && (
                <form.Field name="extract_variables" mode="array">
                  {(varField) => (
                    <div className="flex items-center gap-6 mb-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="button-define-variables"
                        type="button"
                        className="w-fit gap-2"
                        disabled={!isEditing}
                        onClick={() => {
                          varField.pushValue({
                            variable_name: '',
                            variable_instructions: '',
                            variable_format_instructions: '',
                            variable_type: 'string',
                            variable_enum_values: [],
                            configure_data_format: false,
                            id: crypto.randomUUID(),
                          } as never)
                        }}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        {isV29 ? 'Add input' : 'Define Variables'}
                      </Button>
                      {isV29 && !isEmbedMode() && (
                        <DetectInputsButton form={form} isEditing={isEditing} />
                      )}
                    </div>
                  )}
                </form.Field>
              )}
              <FieldsRenderer<FormValues>
                form={form}
                fields={[
                  {
                    name: 'extract_variables',
                    type: 'array',
                    className: 'flex flex-col gap-4 bg-bg3 p-4 rounded-md mb-4 relative',
                    // v2.10 rows come and go with the instructions, as the add button does
                    removeItemButton: (!isV210 || isEmbedMode()) && (
                      <button
                        type="button"
                        className="absolute right-2 top-3 text-destructive hover:text-destructive/80"
                        aria-label="Remove variable"
                      >
                        <Trash2 size={18} strokeWidth={1.5} />
                      </button>
                    ),
                    defaultNewValue: {
                      variable_name: '',
                      variable_instructions: '',
                      variable_format_instructions: '',
                      variable_type: 'string',
                      variable_enum_values: [],
                      configure_data_format: false,
                    },
                    subFields: [
                      {
                        name: 'extract_variables[0].variable_name',
                        componentType: 'input',
                        label: isV29 ? 'Input name' : 'Variable name',
                        zodValidation: z
                          .string({ invalid_type_error: 'Variable name is required' })
                          .min(1, {
                            message: 'Variable name is required',
                          }),
                        eventDataExtractor: (e) =>
                          z
                            .string()
                            .transform((val) => val.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase())
                            .parse(e.target?.value),
                        inputProps: {
                          placeholder: isV29
                            ? 'e.g. callback_phone'
                            : 'Enter variable name to extract',
                        },
                        ...(isV210 && !isEmbedMode()
                          ? { afterOnChange: markAuthored('name') }
                          : {}),
                      },
                      {
                        name: 'extract_variables[0].variable_instructions',
                        type: 'sub-item',
                        componentType: 'textarea',
                        // v2.10 writes it on save, so it may be empty until then; embed skips that
                        ...(isV210 && !isEmbedMode()
                          ? { afterOnChange: markAuthored('description') }
                          : {
                              zodValidation: z
                                .string({
                                  invalid_type_error: 'Variable instructions are required',
                                })
                                .min(1, {
                                  message: 'Variable instructions are required',
                                }),
                            }),
                        inputProps: {
                          label:
                            isV29 || isV210
                              ? 'Description (what the agent asks the caller for)'
                              : 'Variable instructions',
                          placeholder:
                            isV210 && !isEmbedMode()
                              ? describingDots
                                ? `Generating description${'.'.repeat(describingDots)}`
                                : 'Written when you save'
                              : isV29
                                ? 'e.g. The number to call the caller back on'
                                : 'Variable description (e.g. extract the users email from the conversation)',
                          disabled: describingDots > 0,
                          className: `${isV29 || isV210 ? 'min-h-[60px]' : 'min-h-[120px]'} bg-background${describingDots ? ' placeholder:italic' : ''}`,
                          'data-testid': 'textarea-variable-instructions',
                        },
                      },
                      // V2.9 and v2.10: the Input's collection configuration.
                      ...(isV29 || isV210
                        ? [
                            {
                              name: 'extract_variables[0].collection',
                              type: 'sub-item' as const,
                              component: CollectionConfiguration,
                              eventDataExtractor: (v: unknown) => v,
                            },
                          ]
                        : []),
                    ],
                  },
                ]}
              />
            </TabsContent>
            {showActionsTab && (
              <TabsContent
                value="actions"
                forceMount
                className="data-[state=inactive]:hidden actions"
              >
                <AiAgentV2Actions form={form} nodeVars={nodeVars} isEditing={isEditing} />
              </TabsContent>
            )}
          </Tabs>
        )
      },
    },
  ]
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const AiAgentV2SidebarComponent = (props: SidebarComponentProps<AiAgentV2NodeData>) => {
  const { nodeVars } = props
  const { selectedNode } = props
  const setAgentMeta = useFlowStore((state) => state.setAgentMeta)
  const { slackTriggerEnabled } = useFeatureFlags()
  const updateNodeInternals = useUpdateNodeInternals()
  const queryClient = useQueryClient()
  const suggestCollection = useSuggestCollectionConfigurationMutation()
  const setCollectionDetection = useFlowStore((state) => state.setCollectionDetection)
  const writeDetection = useFlowStore((state) => state.writeDetection)
  const { getNode, getEdges, updateNodeData } = useTypedReactFlow()
  // the editor routes the flow as :id, the run canvas as :flowId
  const { id, flowId } = useParams()
  const isV210 =
    selectedNode.type === FlowNodeTypes.ai_agent_v2 && isV210Flow(queryClient, id ?? flowId)

  const [describing, setDescribing] = useState(false)
  const [dots, setDots] = useState(2)
  useEffect(() => {
    if (!describing) return
    const timer = setInterval(() => setDots((n) => (n === 4 ? 2 : n + 1)), 400)
    return () => clearInterval(timer)
  }, [describing])
  const [filling, setFilling] = useState(false)
  const saving = useFlowStore(
    (state) => isV210 && state.collectionDetection.running === selectedNode.id,
  )
  const formFields = fields(
    nodeVars,
    setAgentMeta,
    slackTriggerEnabled,
    selectedNode,
    isV210,
    describing ? dots : 0,
    filling,
    saving,
    (form) => describeFilled(fill(form)),
  )

  // Use saved config if it exists, otherwise use agentMeta for new nodes
  const hasSavedChannel = selectedNode.data.config.channel
  const isV29 = selectedNode.type === FlowNodeTypes.ai_agent_v29

  // Detection is a suggestion the author can delete, so it runs once per node per session:
  // sidebar.base re-selects the node after a save and the sidebar remounts.
  const detectedInputs = useMemo(() => {
    const { isEditing, inputsSeededNodeIds } = useFlowStore.getState()
    const { extract_variables, instructions } = selectedNode.data.config
    if (!isV29 || !isEditing || inputsSeededNodeIds.has(selectedNode.id)) return []
    if (extract_variables?.length || !instructions) return []
    return withDetectedInputs(instructions, [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isV29, selectedNode.id, selectedNode.data.config])

  const markInputsSeeded = useFlowStore((state) => state.markInputsSeeded)
  const markInputsProposed = useFlowStore((state) => state.markInputsProposed)
  const markInputsProposalOwed = useFlowStore((state) => state.markInputsProposalOwed)
  useEffect(() => {
    if (detectedInputs.length) markInputsSeeded(selectedNode.id)
  }, [detectedInputs, markInputsSeeded, selectedNode.id])

  // the errors are keyed by Input name alone, so they belong to this node only
  useEffect(() => {
    setCollectionDetection({ errors: {}, failure: null, notice: null })
  }, [selectedNode.id, setCollectionDetection])

  // Memoize defaultValues based on selectedNode and actual flow state
  const defaultValues = useMemo(() => {
    const currentAgentMeta = useFlowStore.getState().agentMeta
    const rf = useFlowStore.getState().flowInstance

    // V2.9 is voice-only, so it ignores the agent's default channel.
    const defaultChannel = isV29
      ? 'call'
      : hasSavedChannel
        ? selectedNode.data.config.channel
        : currentAgentMeta.defaultChannel

    // Determine the actual voicemail detection value based on current flow state
    // For outbound call flows, voicemail detection is always enabled (defaults to end_conversation)
    const isOutboundCall = shouldShowOutboundCallOnlyFields(defaultChannel ?? '')
    let voicemailDetectionValue =
      selectedNode.data.config.voicemail_detection_next_node ||
      (isOutboundCall ? FlowNodeTypes.end_conversation : undefined)

    if (rf && selectedNode.data.config.channel === 'call') {
      const intents = selectedNode.data.config.intents || []
      const voiceMailIntent = getVoicemailIntent(intents)

      if (voiceMailIntent) {
        const voiceMailNode = getConnectedVoicemailNode(rf, selectedNode.id, voiceMailIntent.id)
        voicemailDetectionValue = voiceMailNode
          ? voiceMailNode.type || FlowNodeTypes.end_conversation
          : FlowNodeTypes.end_conversation
      } else if (isOutboundCall) {
        // Auto-wire the voicemail branch for outbound flows here, before the
        // form mounts, so the form's intents seed carries the new intent
        selectedNode.data.config.intents = syncVoicemailTarget(
          rf,
          selectedNode,
          intents,
          FlowNodeTypes.end_conversation,
        )
      }
    }

    // Chat/SMS mirror of the voicemail branch: derive the reply-timeout
    // dropdown value from the node wired to the "no reply" port, auto-wiring
    // End Conversation for waiting nodes that don't have the branch yet (so
    // older flows pick it up on open, like voicemail does for outbound).
    const savedWaitForResponse = selectedNode.data.config.wait_for_response ?? false
    let noReplyNextNodeValue =
      selectedNode.data.config.no_reply_next_node ||
      (savedWaitForResponse ? FlowNodeTypes.end_conversation : undefined)

    if (rf && channelSupportsTimeoutBranch(selectedNode.data.config.channel)) {
      const connected = getConnectedNoReplyNode(rf, selectedNode.id)
      if (connected?.node) {
        noReplyNextNodeValue =
          (connected.node.type as FlowNodeTypes) || FlowNodeTypes.end_conversation
      } else if (savedWaitForResponse && useFlowStore.getState().isEditing) {
        // Auto-wire only while the flow is editable — inspecting a node in
        // the read-only view must never mutate the canvas.
        syncNoReplyTarget(rf, selectedNode, FlowNodeTypes.end_conversation)
        noReplyNextNodeValue = FlowNodeTypes.end_conversation
      }
    }

    const waitTimeValue = selectedNode.data.config.initial_wait_time
      ? selectedNode.data.config.initial_wait_time
      : 0
    const initialWaitTime =
      typeof waitTimeValue === 'number' && Number.isFinite(waitTimeValue)
        ? waitTimeValue
        : DEFAULT_INITIAL_WAIT_TIME

    // Patch actions loaded from backend with frontend-only fields
    const savedActions = (selectedNode.data.config.actions || []).map((action, i) => ({
      ...action,
      is_expanded: false,
      name: action.name || `Action ${i + 1}`,
    }))

    const baseDefaults = {
      channel: defaultChannel,
      ...(detectedInputs.length ? { extract_variables: detectedInputs as never } : {}),
      voicemail_detection_next_node: voicemailDetectionValue,
      initial_wait_time: initialWaitTime,
      model_config: {
        model: DEFAULT_LLM_MODEL_ID,
        provider: DEFAULT_LLM_PROVIDER,
      },
      update_similar_nodes: false,
      actions: savedActions,
      allow_greeting_interruption: selectedNode.data.config.allow_greeting_interruption ?? true,
    }

    // Only include wait_for_response and reply_timeout for non-call channels
    if (defaultChannel === 'call') {
      return baseDefaults
    }

    return {
      ...baseDefaults,
      wait_for_response: selectedNode.data.config.wait_for_response ?? false,
      reply_timeout: selectedNode.data.config.reply_timeout ?? 3600,
      no_reply_next_node: noReplyNextNodeValue,
    }
  }, [selectedNode, isV29, hasSavedChannel, detectedInputs])

  const applyFormSave = (data: FormValues) => {
    const rf = useFlowStore.getState().flowInstance
    // The AI-greets-on-entry control is not shipped: never author the flag
    // from the builder, and drop any stale value when a flow is re-saved.
    delete (data as Partial<FormValues>).ai_speaks_first
    // Remove wait_for_response and reply_timeout for call channel (not applicable)
    if (data.channel === 'call') {
      delete (data as Partial<FormValues>).wait_for_response
      delete (data as Partial<FormValues>).reply_timeout
      delete (data as Partial<FormValues>).no_reply_next_node
    }
    // Strip actions only for channels that don't support them (whatsapp — its
    // runtime leg is unverified). Call, chat and SMS keep actions so they
    // reach the agent as tools.
    const channelSupportsActions =
      data.channel === 'call' || data.channel === 'chat' || data.channel === 'message'
    if (!channelSupportsActions) {
      delete (data as Partial<FormValues>).actions
    }

    // Validate actions for any channel that supports them.
    if (channelSupportsActions && data.actions?.length) {
      const sidebarForm = useFlowStore.getState()
        .sidebarFormInstance as GenericFormType<FormValues> | null
      if (sidebarForm && !validateAiAgentV2Actions(sidebarForm)) {
        return data
      }
    }

    // Chat/SMS mirror of the voicemail sync below: keep the reply-timeout
    // branch in step with the form. A waiting node always has a target (End
    // Conversation by default); turning the wait off removes the branch edge.
    // A channel that doesn't support the branch (call, whatsapp) removes it
    // too — deleting only the config field would strand a hidden edge and its
    // auto-created target on the canvas.
    if (rf) {
      if (channelSupportsTimeoutBranch(data.channel) && data.wait_for_response) {
        if (!data.no_reply_next_node) {
          data.no_reply_next_node = FlowNodeTypes.end_conversation
        }
        // An SMS preset with no from/to derives the pair from the walk
        // payload un-swapped and replies to the org number — stamp the
        // canonical reply variables so the target is correct even unopened.
        const targetDefaults =
          data.channel === 'message' &&
          nodeVars.includes('{{Start.message.to}}') &&
          nodeVars.includes('{{Start.message.from}}')
            ? { from: '{{Start.message.to}}', to: '{{Start.message.from}}' }
            : undefined
        syncNoReplyTarget(
          rf,
          selectedNode,
          data.no_reply_next_node as FlowNodeTypes,
          targetDefaults,
        )
      } else {
        delete (data as Partial<FormValues>).no_reply_next_node
        syncNoReplyTarget(rf, selectedNode, null)
      }
      updateNodeInternals(selectedNode.id)
    }

    if (!rf || data.channel !== 'call') return data

    // initial_wait_time default is 3 for first ai node and 0 for other ai nodes
    if (!shouldShowOutboundCallOnlyFields(data.channel) || data.initial_wait_time === undefined) {
      data.initial_wait_time = 0
    } else if (
      typeof data.initial_wait_time !== 'number' ||
      !Number.isFinite(data.initial_wait_time) ||
      data.initial_wait_time < 0
    ) {
      data.initial_wait_time = DEFAULT_INITIAL_WAIT_TIME
    }

    const isOutbound = shouldShowOutboundCallOnlyFields(data.channel)

    // Outbound flows always keep voicemail detection on, defaulting to end_conversation
    if (!data.voicemail_detection_next_node && isOutbound) {
      data.voicemail_detection_next_node = FlowNodeTypes.end_conversation
    }

    const desiredType = (data.voicemail_detection_next_node as FlowNodeTypes) || null
    data.intents = syncVoicemailTarget(rf, selectedNode, data.intents || [], desiredType)

    updateNodeInternals(selectedNode.id)
    return data
  }

  const savedConfig = selectedNode.data.config as {
    instructions?: string
    extract_variables?: InputRowValue[]
  }
  // row id -> the name, type and description it was written with
  const describedFor = useRef<Map<string, string>>()
  const describeRun = useRef(0)
  // a closed panel's late answer must not write into the panel opened after it
  const closed = useRef(false)
  useEffect(() => {
    closed.current = false
    return () => {
      closed.current = true
    }
  }, [])
  // the rows Jev said an edit touched: their descriptions are written again
  const redo = useRef<Set<string>>()
  // a name the description writer gave -> the row's name at the last save, whose references move to it
  const renamedFrom = useRef<Map<string, string>>()
  // kept: the author's text, a row Jev never typed, others until an edit touches them or they change
  const isCurrent = (row: InputRowValue) =>
    !!row.variable_instructions &&
    (!!row.collection?.overrides?.description ||
      !row.collection?.type ||
      (!redo.current?.has(nameOf(row)) &&
        (describedFor.current?.get(row.id ?? nameOf(row)) === keyOf(row) ||
          (savedConfig.extract_variables ?? []).some((had) => keyOf(had) === keyOf(row)))))

  // one request for the rows that need it; each box fills when the answer lands
  const describeRows = async (instructions: string, rows: InputRowValue[], authId: string) => {
    const stale = rows.filter((row) => nameOf(row) && !isCurrent(row))
    const written: Record<string, string> = {}
    const renamed: Record<string, string> = {}
    if (!stale.length) return { written, renamed, missing: stale }
    const run = ++describeRun.current
    const existing = Object.fromEntries(
      rows.flatMap((row) =>
        nameOf(row) && row.collection?.type
          ? [[nameOf(row), { ...toWire(row.collection), version: null }]]
          : [],
      ),
    )
    // only into this node's open form, from the latest run, onto the row still standing where it was
    const show = (row: InputRowValue, text: string, field = 'variable_instructions') => {
      const { selectedNodeID, sidebarFormInstance } = useFlowStore.getState()
      const live = sidebarFormInstance as GenericFormType<FormValues> | null
      const index = rows.indexOf(row)
      const shown = live?.getFieldValue('extract_variables') as InputRowValue[] | undefined
      if (
        run === describeRun.current &&
        !closed.current &&
        selectedNodeID === selectedNode.id &&
        shown?.[index] &&
        nameOf(shown[index]) === nameOf(row)
      ) {
        const name = `extract_variables[${index}].${field}` as never
        live?.setFieldValue(name, text as never)
        // a field set before its tab mounts gets no errorMap, and TanStack then throws on its first update
        live?.setFieldMeta(name, ((meta?: { errorMap?: object; errors?: unknown[] }) => ({
          ...meta,
          errorMap: meta?.errorMap ?? {},
          errors: meta?.errors ?? [],
        })) as never)
      }
    }
    for (const row of stale) show(row, '')
    setDots(2)
    setDescribing(true)
    try {
      // one request; the runner makes one model call per variable, all at once
      const { descriptions = {}, names = {} } = await suggestCollection
        .mutateAsync({
          auth_id: authId,
          instructions,
          // every row, so a second phone or date is told apart from the first; a blank one is written
          inputs: rows.flatMap((row) =>
            nameOf(row)
              ? [
                  {
                    variable_name: nameOf(row),
                    variable_instructions: stale.includes(row)
                      ? ''
                      : (row.variable_instructions ?? ''),
                    authored: authoredOf(row),
                  },
                ]
              : [],
          ),
          existing,
          describe: true,
        })
        .catch(() => ({
          descriptions: {} as Record<string, string>,
          names: {} as Record<string, string>,
        }))
      for (const row of stale) {
        const text = descriptions[nameOf(row)] ?? ''
        if (!text) continue
        const to = names[nameOf(row)]
        written[nameOf(row)] = text
        redo.current?.delete(nameOf(row))
        describedFor.current ??= new Map()
        describedFor.current.set(
          row.id ?? to ?? nameOf(row),
          keyOf({ ...row, variable_name: to ?? nameOf(row), variable_instructions: text }),
        )
        show(row, text)
        if (!to) continue
        // after the description: show finds the row by the name it had
        show(row, to, 'variable_name')
        renamed[nameOf(row)] = to
        renamedFrom.current ??= new Map()
        renamedFrom.current.set(to, renamedFrom.current.get(nameOf(row)) ?? nameOf(row))
      }
    } finally {
      if (run === describeRun.current) setDescribing(false)
    }
    const failed = stale.filter((row) => !written[nameOf(row)])
    for (const row of failed) show(row, row.variable_instructions ?? '')
    return { written, renamed, missing: failed.filter((row) => !row.variable_instructions) }
  }
  // a save moves every {{node.old}} downstream to the name the description writer gave since the last save
  const moveReferences = (rows: InputRowValue[]) => {
    const node = (getNode(selectedNode.id)?.data.config as { name?: string } | undefined)?.name
    for (const row of rows) {
      const was = renamedFrom.current?.get(nameOf(row))
      if (node && was)
        scheduleNodeVariableReferenceRewrite(
          selectedNode.id,
          `${node}.${was}`,
          `${node}.${nameOf(row)}`,
          updateNodeData,
          () => getEdges(),
        )
    }
    renamedFrom.current?.clear()
    return rows
  }

  // kept off the rows: the runner refuses unknown row keys. No typed row yet means never filled.
  const lastFill = useRef(
    (savedConfig.extract_variables ?? []).some((row) => row.collection?.type)
      ? wording(savedConfig.instructions ?? '')
      : '',
  )
  // row id -> its signature when a fill made it
  const filled = useRef<Map<string, string>>()
  const asking = useRef(false)
  const nothingFor = useRef('')
  // the instructions a fill failed for: their save is refused once
  const failedFor = useRef<string>()
  const fill = async (
    form: GenericFormType<FormValues>,
  ): Promise<readonly [string, InputRowValue[]] | undefined> => {
    const text = (form.getFieldValue('instructions') ?? '') as string
    const asked = wording(text)
    // deleting every variable is a reset, so unchanged instructions fill again
    const named = ((form.getFieldValue('extract_variables') ?? []) as InputRowValue[]).some(nameOf)
    // an answer with no variable is remembered, or such a node would ask on every blur
    const settled = named || nothingFor.current === asked
    // instructions that held text and were emptied clear the variables, also ones no fill has typed yet
    const emptied =
      !asked && named && !!(lastFill.current || wording(savedConfig.instructions ?? ''))
    if (!isV210 || isEmbedMode() || (asked === lastFill.current && settled && !emptied)) return
    // one request at a time
    if (!useFlowStore.getState().isEditing || asking.current) return
    if (!asked) {
      lastFill.current = ''
      form.setFieldValue('extract_variables', [] as never)
      return
    }
    const previous = lastFill.current
    lastFill.current = asked
    // not the store flag, which disables Save: the click that blurs the box is often on Save
    asking.current = true
    setFilling(true)
    writeDetection(selectedNode.id, { errors: {}, failure: null, notice: null })
    const done = () => {
      setFilling(false)
      asking.current = false
    }
    const authId = orgAuthId(queryClient)
    // an edit of a filled node goes to Jev as an edit; a first fill reads the whole text
    const editing = !!previous && named && !!authId
    const answer = editing
      ? await detectChanges({
          authId: authId as string,
          instructions: text,
          previous,
          rows: (form.getFieldValue('extract_variables') ?? []) as InputRowValue[],
          suggest: suggestCollection.mutateAsync,
        }).finally(done)
      : undefined
    const result = editing
      ? { rows: [], errors: {}, notice: null, failure: answer ? null : 'not reached' }
      : await detectCollections({
          authId,
          instructions: text,
          rows: [] as InputRowValue[],
          suggest: suggestCollection.mutateAsync,
          propose: true,
          engine: 'jev',
          newRow: blankInputRow,
        }).finally(done)
    // an answer about older instructions is dropped, and the text the box holds now is asked
    const stale = wording((form.getFieldValue('instructions') ?? '') as string) !== asked
    if (stale || result.failure) lastFill.current = previous
    // the panel that asked is closed: nothing to fill, nothing to ask again
    if (closed.current) return
    if (stale) return fill(form)
    // a fill holds no flag, so its notice is written directly, not through writeDetection
    setCollectionDetection({
      errors: result.errors,
      failure: result.failure
        ? 'Could not fill the variables from the instructions. Click outside the instructions box to try again.'
        : null,
      notice: result.notice,
    })
    failedFor.current = result.failure ? asked : undefined
    if (result.failure) return
    const rows = (form.getFieldValue('extract_variables') ?? []) as InputRowValue[]
    if (answer) {
      const changed = applyChanges(rows, answer, text, blankInputRow)
      redo.current ??= new Set()
      for (const name of changed.redo) redo.current.add(name)
      form.setFieldValue('extract_variables', changed.rows as never)
      nothingFor.current = changed.rows.length ? '' : asked
      return [text, changed.rows] as const
    }
    filled.current ??= new Map()
    form.setFieldValue('extract_variables', mergeFilled(rows, filled.current, result.rows) as never)
    // added to, never replaced: a row kept because it was edited keeps the signature it was filled with
    for (const row of result.rows) filled.current.set(row.id ?? '', rowSignature(row))
    nothingFor.current = result.rows.length ? '' : asked
    return [text, form.getFieldValue('extract_variables') as InputRowValue[]] as const
  }

  // the descriptions start as soon as a fill lands; a save waits for the fill and for them
  const inflight = useRef<Promise<unknown>>()
  const describeFilled = (landing: ReturnType<typeof fill>) => {
    const earlier = inflight.current
    inflight.current = landing
      .then((landed) => {
        const authId = landed && orgAuthId(queryClient)
        return landed && authId ? describeRows(...landed, authId) : undefined
      })
      .catch(() => {})
      .then(() => earlier)
  }

  const describeVariables = async (applied: FormValues) => {
    const refuse = (description: string): never => {
      errorToast({ title: 'The node was not saved', description })
      throw new Error(description)
    }
    // Save shows its spinner while it works, so a second click cannot start another save
    setCollectionDetection({ running: selectedNode.id })
    try {
      await inflight.current
      // the rows are not for these instructions then, and a save would close the panel over the notice
      if (failedFor.current === wording((applied.instructions ?? '') as string)) {
        failedFor.current = undefined
        refuse(
          'The variables could not be filled from the instructions. Save again to keep them as they are.',
        )
      }
      const { selectedNodeID, sidebarFormInstance } = useFlowStore.getState()
      // the open form holds what a run wrote after the submit
      const live =
        selectedNodeID === selectedNode.id
          ? (sidebarFormInstance as GenericFormType<FormValues> | null)?.state.values
              .extract_variables
          : undefined
      const rows = ((live ?? applied.extract_variables ?? []) as InputRowValue[]).map((row) =>
        row.collection ? { ...row, collection: toSaved(row.collection) } : row,
      )
      const instructions = (applied.instructions ?? '') as string
      if (rows.every((row) => !nameOf(row) || isCurrent(row)))
        return { ...applied, extract_variables: moveReferences(rows) } as FormValues
      const authId =
        orgAuthId(queryClient) ?? refuse('This account has no auth id yet. Reload the builder.')
      const { written, renamed, missing } = await describeRows(instructions, rows, authId)
      if (missing.length)
        refuse(`No description was written for: ${missing.map(nameOf).join(', ')}`)
      writeDetection(selectedNode.id, { errors: {}, failure: null, notice: null })
      const described = rows.map((row) => ({
        ...row,
        ...(renamed[nameOf(row)] ? { variable_name: renamed[nameOf(row)] } : {}),
        ...(written[nameOf(row)] ? { variable_instructions: written[nameOf(row)] } : {}),
      }))
      return { ...applied, extract_variables: moveReferences(described) } as FormValues
    } finally {
      endDetection(selectedNode.id)
    }
  }

  // Save fills in every Input whose configuration is missing or out of date; author overrides win.
  const handleFormSave = async (data: FormValues) => {
    const applied = applyFormSave(data)
    if (isV210 && !isEmbedMode()) return describeVariables(applied)
    // Embed has no gateway rule for the runner's suggest endpoint: save the Inputs as authored.
    if (!isV29 || isEmbedMode()) return applied
    const task = (applied.instructions ?? '') as string
    const asked = (applied.extract_variables ?? []) as InputRowValue[]
    // the Inputs were read from the saved Task: a changed Task asks again, so does a save whose ask
    // failed, and a node with none asks once per session
    const savedTask = (selectedNode.data.config as { instructions?: string }).instructions ?? ''
    const named = asked.some((row) => (row.variable_name ?? '').trim())
    const { inputsProposed, inputsProposalOwed } = useFlowStore.getState()
    const proposed = inputsProposed.get(selectedNode.id)
    const discover =
      wording(savedTask) !== wording(task) ||
      inputsProposalOwed.has(selectedNode.id) ||
      (!named && !proposed)
    // the row controls stay live while this is in flight, so bind the answer to what was asked
    const request = requestFor(selectedNode.id, asked, task)
    setCollectionDetection({ running: selectedNode.id })
    writeDetection(selectedNode.id, { errors: {}, failure: null, notice: null })
    try {
      const { rows, errors, failure, notice } = await detectCollections({
        authId: orgAuthId(queryClient),
        instructions: task,
        rows: asked,
        suggest: suggestCollection.mutateAsync,
        ...(discover ? { propose: true, newRow: blankInputRow } : {}),
      })
      // the save closes the sidebar, so the form often shows another node by now: its rows
      // are not this node's, and nothing about this node changed while it was away
      const live =
        useFlowStore.getState().selectedNodeID === selectedNode.id
          ? (useFlowStore.getState().sidebarFormInstance as GenericFormType<FormValues> | null)
              ?.state.values
          : undefined
      const now = (live?.extract_variables ?? asked) as InputRowValue[]
      const added = new Set(addedBy(asked, rows))
      // a proposal the author already saw this session is not put back, deleted or renamed
      const fresh = rows.filter((row) => !added.has(row) || !proposed?.has(nameOf(row)))
      const merged = reconcileDetected(
        now,
        fresh,
        request,
        request.nodeId,
        (live?.instructions ?? task) as string,
      )
      // an answer about an older Task is not about what is being saved: the edits stand alone
      if (merged) writeDetection(selectedNode.id, { errors, failure, notice })
      else
        writeDetection(selectedNode.id, { errors: {}, failure: null, notice: STALE_ANSWER_NOTICE })
      if (discover && merged && !failure)
        markInputsProposed(selectedNode.id, [...added].map(nameOf))
      else if (discover) markInputsProposalOwed(selectedNode.id)
      const kept = merged ?? now
      // the sidebar closes on save, so the banner never paints: the toast is the only surface left
      const unconfigured = kept.flatMap((row) => {
        const name = (row.variable_name ?? '').trim()
        return name && (!row.collection?.type || row.collection.stale === true) ? [name] : []
      })
      if (!merged || failure || unconfigured.length)
        errorToast({
          title: 'Some Inputs were saved with no collection configuration',
          description: [
            merged ? failure : STALE_ANSWER_NOTICE,
            unconfigured.length ? `Not configured: ${unconfigured.join(', ')}` : '',
          ]
            .filter(Boolean)
            .join(' '),
        })
      // the save boundary: the runner's report about its answer never reaches the saved row
      const saved = kept.map((row) =>
        row.collection ? { ...row, collection: toSaved(row.collection) } : row,
      )
      return { ...applied, extract_variables: saved }
    } finally {
      endDetection(selectedNode.id)
    }
  }

  return (
    <GenericSidebarComponent
      defaultValues={defaultValues}
      fields={formFields}
      callback={handleFormSave}
      {...props}
    />
  )
}
